import React from "react";
import { Server, Cloud, ArrowRight, Check } from "lucide-react";

const coreFeatures = [
  "Hash with rotating salt",
  "Blacklist & TAC filter",
  "Configurable anonymisation thresholds (k>5 default)",
  "Automated validation at egress",
];

const cityFeatures = [
  "Activities: location-based patterns",
  "OD Matrices: origin and destination",
  "Spatial Flows: road and link volumes",
  "Real-time monitoring",
  "Bespoke extracts via API",
];

const OperatorBoundaryDiagram = () => {
  /* Three vertical bands: operator zone | boundary band | telcofy zone.
     The boundary band is sized so the crossing arrow + caption text fit
     comfortably without spilling into either zone. */
  const VB_W = 820;
  const VB_H = 240;

  // Operator zone
  const opX = 10;
  const opW = 270;

  // Boundary band
  const boundaryX = opX + opW; // 280
  const boundaryW = 240;        // 280 → 520 — wide enough for the caption
  const boundaryCenter = boundaryX + boundaryW / 2; // 400

  // Telcofy zone
  const tcX = boundaryX + boundaryW; // 520
  const tcW = 290;

  // Boxes (TelcofyCore on the left, CityOS on the right)
  const boxY = 90;
  const boxH = 90;
  const coreX = 40;
  const coreW = 210;
  const cityX = 555;
  const cityW = 220;

  // Arrow line (centred vertically across boxes, sits inside the boundary band)
  const arrowY = boxY + boxH / 2; // 135
  const arrowX1 = boundaryX + 18; // 298
  const arrowX2 = boundaryX + boundaryW - 18; // 502

  return (
    <svg
      viewBox={`0 0 ${VB_W} ${VB_H}`}
      className="w-full h-auto"
      role="img"
      aria-label="Operator boundary diagram: TelcofyCore inside the operator's environment, CityOS outside on Telcofy infrastructure"
    >
      {/* Operator zone */}
      <rect x={opX} y="40" width={opW} height="170" rx="12" fill="#F2F2F2" stroke="#E5E6E6" />
      <text
        x={opX + 18}
        y="64"
        fontFamily="Poppins, Inter, sans-serif"
        fontWeight="600"
        fontSize="11"
        fill="#6B7280"
        letterSpacing="2"
      >
        OPERATOR INFRASTRUCTURE
      </text>

      {/* TelcofyCore */}
      <rect
        x={coreX}
        y={boxY}
        width={coreW}
        height={boxH}
        rx="8"
        fill="#FFFFFF"
        stroke="#4CAF50"
        strokeWidth="1.5"
      />
      <text
        x={coreX + coreW / 2}
        y={boxY + 32}
        textAnchor="middle"
        fontFamily="Poppins, Inter, sans-serif"
        fontWeight="700"
        fontSize="15"
        fill="#1E1E1E"
      >
        TelcofyCore
      </text>
      <text
        x={coreX + coreW / 2}
        y={boxY + 52}
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontSize="11"
        fill="#6B7280"
      >
        Privacy engine
      </text>
      <text
        x={coreX + coreW / 2}
        y={boxY + 70}
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontSize="11"
        fill="#6B7280"
      >
        Anonymisation pipeline
      </text>

      {/* Telcofy zone */}
      <rect
        x={tcX}
        y="40"
        width={tcW}
        height="170"
        rx="12"
        fill="#E8F5E9"
        stroke="#4CAF50"
        opacity="0.95"
      />
      <text
        x={tcX + 18}
        y="64"
        fontFamily="Poppins, Inter, sans-serif"
        fontWeight="600"
        fontSize="11"
        fill="#388E3C"
        letterSpacing="2"
      >
        TELCOFY INFRASTRUCTURE
      </text>

      {/* CityOS */}
      <rect
        x={cityX}
        y={boxY}
        width={cityW}
        height={boxH}
        rx="8"
        fill="#FFFFFF"
        stroke="#4CAF50"
        strokeWidth="1.5"
      />
      <text
        x={cityX + cityW / 2}
        y={boxY + 32}
        textAnchor="middle"
        fontFamily="Poppins, Inter, sans-serif"
        fontWeight="700"
        fontSize="15"
        fill="#1E1E1E"
      >
        CityOS
      </text>
      <text
        x={cityX + cityW / 2}
        y={boxY + 52}
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontSize="11"
        fill="#6B7280"
      >
        Analytics workbench
      </text>
      <text
        x={cityX + cityW / 2}
        y={boxY + 70}
        textAnchor="middle"
        fontFamily="Inter, sans-serif"
        fontSize="11"
        fill="#6B7280"
      >
        API · dashboards
      </text>

      {/* Boundary line (full height of the band) */}
      <line
        x1={boundaryCenter}
        y1="20"
        x2={boundaryCenter}
        y2="220"
        stroke="#1E1E1E"
        strokeWidth="1"
        strokeDasharray="3 4"
        opacity="0.55"
      />
      <text
        x={boundaryCenter}
        y="14"
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize="9.5"
        fill="#1E1E1E"
        fontWeight="600"
        letterSpacing="1"
      >
        OPERATOR BOUNDARY
      </text>

      {/* Crossing arrow + caption, isolated inside the boundary band. */}
      <text
        x={boundaryCenter}
        y={arrowY - 22}
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize="10"
        fill="#388E3C"
        fontWeight="600"
      >
        anonymised
      </text>
      <text
        x={boundaryCenter}
        y={arrowY - 10}
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize="10"
        fill="#388E3C"
        fontWeight="600"
      >
        aggregates only
      </text>
      <line
        x1={arrowX1}
        y1={arrowY}
        x2={arrowX2 - 8}
        y2={arrowY}
        stroke="#4CAF50"
        strokeWidth="2"
      />
      <polygon
        points={`${arrowX2 - 10},${arrowY - 5} ${arrowX2},${arrowY} ${arrowX2 - 10},${arrowY + 5}`}
        fill="#4CAF50"
      />

      {/* Footer captions in their own band beneath the zones */}
      <text
        x={opX + opW / 2}
        y="232"
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize="10"
        fill="#6B7280"
        letterSpacing="1.5"
      >
        SOLD TO OPERATORS
      </text>
      <text
        x={tcX + tcW / 2}
        y="232"
        textAnchor="middle"
        fontFamily="ui-monospace, monospace"
        fontSize="10"
        fill="#388E3C"
        letterSpacing="1.5"
      >
        SOLD TO END USERS
      </text>
    </svg>
  );
};

const TwoProductsSection = () => {
  return (
    <section id="products" className="py-24 bg-paper">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-3xl mb-12">
          <p className="text-xs font-semibold tracking-widest text-brand-600 uppercase mb-3">
            Platform
          </p>
          <h2 className="font-display text-4xl md:text-5xl font-bold text-ink tracking-tight mb-4">
            Two platforms. Two buyers. One pipeline between them.
          </h2>
          <p className="text-lg text-ink-muted leading-relaxed">
            <strong className="text-ink">TelcofyCore</strong> is the privacy
            engine that operators install inside their own infrastructure.
            Sensitive data never leaves the operator.{" "}
            <strong className="text-ink">CityOS</strong> is the analytics
            workbench that cities, statistical offices, and commercial partners
            log into. Each is sold separately to its own audience; together
            they form a single, neutral, multi-operator pipeline.
          </p>
        </div>

        {/* Diagram */}
        <div className="bg-paper border border-paper-300 rounded-lg p-6 md:p-8 mb-10 shadow-brand-sm">
          <OperatorBoundaryDiagram />
        </div>

        {/* Two product cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {/* TelcofyCore */}
          <div className="bg-paper border border-paper-300 rounded-lg p-7">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-paper-200 grid place-items-center">
                <Server size={20} className="text-ink-soft" />
              </div>
              <span className="text-[11px] font-mono text-ink-subtle uppercase tracking-wider">
                Sold to operators
              </span>
            </div>
            <h3 className="font-display text-2xl font-semibold text-ink mb-1">
              TelcofyCore
            </h3>
            <p className="text-xs font-medium text-ink-subtle mb-3">
              The privacy engine. Runs inside the operator boundary.
            </p>
            <p className="text-sm text-ink-muted leading-relaxed mb-5">
              Ingests raw network signals, applies the configurable privacy
              stack, and emits only anonymised aggregates. The operator's
              CISO and DPO control thresholds; sensitive data never leaves
              their infrastructure.
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-subtle mb-2">
              Built for
            </p>
            <p className="text-sm text-ink-muted mb-5">
              MNO commercial &amp; wholesale teams · Group security &amp; data-protection teams
            </p>
            <ul className="space-y-2.5 pt-4 border-t border-paper-300">
              {coreFeatures.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-ink-soft">
                  <Check size={14} className="text-brand-600 mt-1 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* CityOS */}
          <div className="bg-paper border border-brand-500/40 rounded-lg p-7 shadow-brand-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-md bg-brand-50 grid place-items-center">
                <Cloud size={20} className="text-brand-600" />
              </div>
              <span className="text-[11px] font-mono text-brand-700 uppercase tracking-wider">
                Sold to end users
              </span>
            </div>
            <h3 className="font-display text-2xl font-semibold text-ink mb-1">
              CityOS
            </h3>
            <p className="text-xs font-medium text-ink-subtle mb-3">
              The analytics workbench. Hosted by Telcofy.
            </p>
            <p className="text-sm text-ink-muted leading-relaxed mb-5">
              Receives anonymised aggregates from TelcofyCore. Enriches them
              with traffic models, transit, land use, weather, and other
              context. Customers manage projects, draw custom polygons,
              schedule analyses, and download datasets.
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-widest text-ink-subtle mb-2">
              Built for
            </p>
            <p className="text-sm text-ink-muted mb-5">
              Cities &amp; transport authorities · Statistical offices · Resellers and commercial partners
            </p>
            <ul className="space-y-2.5 pt-4 border-t border-paper-300">
              {cityFeatures.map((f) => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-ink-soft">
                  <ArrowRight size={14} className="text-brand-600 mt-1 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TwoProductsSection;
